/**
 * ErrorBoundary — top-level React error boundary for the Word add-in.
 *
 * SRP: This component ONLY catches render-time exceptions and surfaces a
 * recoverable UI. It does NOT format errors, log to multiple sinks, or
 * report bugs — those concerns live in the observability pipeline that
 * we forward into.
 *
 * Agentic UI principles:
 * - Never show a white screen. A crash should look like a designed
 *   "something broke" card with a clear next action.
 * - Surface a short error_id the writer can quote when reporting.
 * - "Reload widget" is the primary action; "Copy details" is secondary.
 * - The crash itself flows into the central logger as `frontend.crashed`
 *   so the same sinks that record everything else also see it.
 */

import React from "react";
import { getLogger } from "../observability";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorId: string | null;
}

function newErrorId(): string {
  // 8-char hex matches the backend AsthraError.error_id format.
  return Math.random().toString(16).slice(2, 10).padEnd(8, "0");
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error, errorId: newErrorId() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Forward to the central logger — flows through ServerSink to the
    // backend's central log file (asthra.log) so dev + ops see the crash
    // alongside backend events under the same correlation surface.
    try {
      getLogger("frontend.errorBoundary").error("frontend.crashed", {
        error_id: this.state.errorId ?? "unknown",
        name: error.name,
        message: error.message,
        stack: error.stack,
        component_stack: info.componentStack,
      });
    } catch {
      /* never let logging block recovery */
    }
  }

  private handleReload = () => {
    // Resetting state lets the writer try the operation again without
    // a hard page reload — if the cause was transient, this is enough.
    this.setState({ error: null, errorId: null });
  };

  private handleHardReload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  private handleCopy = () => {
    const { error, errorId } = this.state;
    if (!error) return;
    const payload = [
      `Error ID: ${errorId ?? "unknown"}`,
      `Name: ${error.name}`,
      `Message: ${error.message}`,
      "",
      "Stack:",
      error.stack ?? "(no stack)",
    ].join("\n");
    try {
      navigator.clipboard?.writeText(payload);
    } catch {
      /* ignore */
    }
  };

  render(): React.ReactNode {
    const { error, errorId } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div
        style={{
          padding: "20px",
          margin: "20px",
          backgroundColor: "#FFF8E1",
          border: "1px solid #F5C842",
          borderRadius: "8px",
          fontFamily: "'Lato', 'Segoe UI', sans-serif",
          fontSize: "13px",
          lineHeight: "1.5",
          color: "#333333",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#8B6914",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: "8px",
          }}
        >
          ⚠ Something went wrong
        </div>
        <div style={{ marginBottom: "8px" }}>
          The widget hit an unexpected error. You can try again, or reload the widget completely. Your work in Word is
          safe.
        </div>
        <div
          style={{
            fontSize: "11px",
            color: "#666666",
            marginBottom: "12px",
          }}
        >
          Error ID: <code style={{ fontFamily: "monospace" }}>err_{errorId}</code>
          {" — "}
          {error.name}: {error.message}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "6px 12px",
              backgroundColor: "#5603AD",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.handleHardReload}
            style={{
              padding: "6px 12px",
              backgroundColor: "#FFFFFF",
              color: "#5603AD",
              border: "1px solid #5603AD",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Reload widget
          </button>
          <button
            type="button"
            onClick={this.handleCopy}
            style={{
              padding: "6px 12px",
              backgroundColor: "transparent",
              color: "#666666",
              border: "1px solid #DDDDDD",
              borderRadius: "4px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Copy details
          </button>
        </div>
      </div>
    );
  }
}
